import React, { useRef, useState } from 'react';
import site from '../../data/site';
import { reels } from '../../data/media';
import { Section, SectionHeading } from '../ui/Section';
import Reveal from '../ui/Reveal';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import ReelPlayer from '../ui/ReelPlayer';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';

/**
 * A reel tile. It previews silently on hover/focus and opens the full player
 * with sound on click — the same interaction pattern people already know from
 * Instagram, which is where these clips come from.
 */
function ReelCard({ reel, index, onOpen }) {
  const videoRef = useRef(null);
  const reduceMotion = usePrefersReducedMotion();

  const preview = (on) => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;
    if (on) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  return (
    <Reveal
      variant="up"
      distance={32}
      duration={880}
      delay={index * 85}
      className="shrink-0"
      style={{ width: 'clamp(13rem, 30vw, 17rem)' }}
    >
      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={() => preview(true)}
        onMouseLeave={() => preview(false)}
        onFocus={() => preview(true)}
        onBlur={() => preview(false)}
        aria-label={`Play reel: ${reel.title}`}
        className="group relative block w-full overflow-hidden rounded-md bg-ink-900 shadow-card"
      >
        <video
          ref={videoRef}
          src={reel.src}
          poster={reel.poster}
          muted
          loop
          playsInline
          preload="none"
          className="aspect-[9/16] w-full object-cover transition-transform duration-700 ease-silk group-hover:scale-[1.03]"
        />

        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/80 via-ink-950/5 to-transparent" />

        {/* Play affordance. */}
        <span className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-canvas/15 text-canvas backdrop-blur-sm transition-all duration-400 ease-silk group-hover:scale-110 group-hover:bg-canvas group-hover:text-ink-950">
            <Icon name="play" size={20} strokeWidth={0} fill="currentColor" className="ml-0.5" />
          </span>
        </span>

        <span className="pointer-events-none absolute inset-x-0 bottom-0 p-4 text-left">
          <span className="block font-display text-lg leading-tight text-canvas">{reel.title}</span>
          <span className="mt-0.5 block text-[0.68rem] leading-snug text-canvas/55">
            {reel.caption}
          </span>
        </span>
      </button>
    </Reveal>
  );
}

/** Horizontal reel rail with arrow paging on desktop. */
export function Reels() {
  const [open, setOpen] = useState(null);
  const railRef = useRef(null);

  const page = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * rail.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <Section id="reels" tone="soft" className="overflow-hidden">
      <div className="shell">
        <SectionHeading
          eyebrow="Reels"
          title="The pieces, in motion"
          lede="Stones move. A photograph flattens them. These are the clips we post to Instagram — tap any one to watch it full screen with sound."
          action={
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-2 lg:flex">
                <button
                  type="button"
                  onClick={() => page(-1)}
                  aria-label="Scroll reels left"
                  className="grid h-11 w-11 place-items-center rounded-full border border-ink-900/15 text-ink-900 transition hover:border-ink-900/50 hover:bg-ink-950 hover:text-canvas"
                >
                  <Icon name="chevronLeft" />
                </button>
                <button
                  type="button"
                  onClick={() => page(1)}
                  aria-label="Scroll reels right"
                  className="grid h-11 w-11 place-items-center rounded-full border border-ink-900/15 text-ink-900 transition hover:border-ink-900/50 hover:bg-ink-950 hover:text-canvas"
                >
                  <Icon name="chevronRight" />
                </button>
              </div>
              <Button href={site.social.instagram} variant="outline" size="md">
                <Icon name="instagram" size={16} />
                Follow
              </Button>
            </div>
          }
        />
      </div>

      <div
        ref={railRef}
        className="no-scrollbar mt-12 flex gap-4 overflow-x-auto px-[var(--shell-x)] pb-3 sm:gap-6"
      >
        {reels.map((reel, i) => (
          <ReelCard key={reel.slug} reel={reel} index={i} onOpen={() => setOpen(i)} />
        ))}
      </div>

      <ReelPlayer reels={reels} index={open} onClose={() => setOpen(null)} onNavigate={setOpen} />
    </Section>
  );
}

export default Reels;
